// Win celebration effects - confetti and sounds

// CSS Keyframes for confetti animation
const confettiStyles = `
@keyframes confetti-fall {
  0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
  100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
}

@keyframes confetti-shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-10px); }
  75% { transform: translateX(10px); }
}

.confetti-piece {
  position: fixed;
  width: 10px;
  height: 10px;
  top: -10px;
  animation: confetti-fall 3s ease-out forwards, confetti-shake 0.5s ease-in-out infinite;
  z-index: 9999;
  pointer-events: none;
}
`;

// Inject styles once
let stylesInjected = false;
const injectStyles = () => {
  if (stylesInjected) return;
  const styleSheet = document.createElement("style");
  styleSheet.textContent = confettiStyles;
  document.head.appendChild(styleSheet);
  stylesInjected = true;
};

// Confetti colors
const CONFETTI_COLORS = [
  '#00E701', // Green
  '#FFD600', // Gold
  '#FF6B00', // Orange
  '#007AFF', // Blue
  '#FF3B30', // Red
  '#FF007A', // Pink
  '#FFFFFF', // White
];

// Create confetti explosion
export const triggerConfetti = (count = 50) => {
  injectStyles();
  
  for (let i = 0; i < count; i++) {
    setTimeout(() => {
      const confetti = document.createElement('div');
      confetti.className = 'confetti-piece';
      confetti.style.left = Math.random() * 100 + 'vw';
      confetti.style.backgroundColor = CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];
      confetti.style.animationDelay = Math.random() * 0.5 + 's';
      confetti.style.animationDuration = (2 + Math.random() * 2) + 's';
      confetti.style.width = (5 + Math.random() * 10) + 'px';
      confetti.style.height = (5 + Math.random() * 10) + 'px';
      confetti.style.borderRadius = Math.random() > 0.5 ? '50%' : '0';
      
      document.body.appendChild(confetti);
      
      // Remove after animation
      setTimeout(() => confetti.remove(), 4000);
    }, i * 20);
  }
};

// Sound effects using Web Audio API
class SoundEffects {
  constructor() {
    this.audioContext = null;
    this.enabled = true;
  }
  
  init() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this.audioContext;
  }
  
  setEnabled(enabled) {
    this.enabled = enabled;
  }
  
  // Generate a win sound
  playWin() {
    if (!this.enabled) return;
    
    try {
      const ctx = this.init();
      
      // Create ascending notes for win
      const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
      
      notes.forEach((freq, i) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        oscillator.type = 'sine';
        oscillator.frequency.value = freq;
        
        const startTime = ctx.currentTime + (i * 0.1);
        const duration = 0.2;
        
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      });
    } catch (e) {
      console.log('Sound not available');
    }
  }
  
  // Generate a big win sound
  playBigWin() {
    if (!this.enabled) return;
    
    try {
      const ctx = this.init();
      
      // Fanfare for big win
      const notes = [
        { freq: 523.25, time: 0 },
        { freq: 659.25, time: 0.1 },
        { freq: 783.99, time: 0.2 },
        { freq: 1046.50, time: 0.3 },
        { freq: 1318.51, time: 0.5 },
        { freq: 1567.98, time: 0.6 },
      ];
      
      notes.forEach(({ freq, time }) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        oscillator.type = 'triangle';
        oscillator.frequency.value = freq;
        
        const startTime = ctx.currentTime + time;
        const duration = 0.3;
        
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(0.4, startTime + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      });
    } catch (e) {
      console.log('Sound not available');
    }
  }
  
  // Generate a loss sound
  playLoss() {
    if (!this.enabled) return;
    
    try {
      const ctx = this.init();
      
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.type = 'sawtooth';
      oscillator.frequency.setValueAtTime(200, ctx.currentTime);
      oscillator.frequency.linearRampToValueAtTime(100, ctx.currentTime + 0.3);
      
      gainNode.gain.setValueAtTime(0.2, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.3);
    } catch (e) {
      console.log('Sound not available');
    }
  }
  
  // Click/button sound
  playClick() {
    if (!this.enabled) return;
    
    try {
      const ctx = this.init();
      
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.type = 'sine';
      oscillator.frequency.value = 800;
      
      gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.05);
    } catch (e) {
      console.log('Sound not available');
    }
  }
  
  // Spin/roll sound
  playSpin() {
    if (!this.enabled) return;
    
    try {
      const ctx = this.init();
      
      for (let i = 0; i < 10; i++) {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        oscillator.type = 'sine';
        oscillator.frequency.value = 300 + (i * 50);
        
        const startTime = ctx.currentTime + (i * 0.05);
        
        gainNode.gain.setValueAtTime(0.05, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.04);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + 0.04);
      }
    } catch (e) {
      console.log('Sound not available');
    }
  }
  
  // Coin/cash sound
  playCoin() {
    if (!this.enabled) return;
    
    try {
      const ctx = this.init();
      
      const notes = [2000, 2500, 3000];
      
      notes.forEach((freq, i) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        oscillator.type = 'sine';
        oscillator.frequency.value = freq;
        
        const startTime = ctx.currentTime + (i * 0.05);
        
        gainNode.gain.setValueAtTime(0.15, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.1);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + 0.1);
      });
    } catch (e) {
      console.log('Sound not available');
    }
  }
}

// Singleton instance
export const soundEffects = new SoundEffects();

// Celebration function combining confetti and sound
export const celebrateWin = (multiplier = 1, amount = 0) => {
  if (multiplier >= 5 || amount >= 1000) {
    // Big win!
    triggerConfetti(100);
    soundEffects.playBigWin();
  } else if (multiplier >= 2 || amount >= 100) {
    // Regular win
    triggerConfetti(50);
    soundEffects.playWin();
  } else {
    // Small win
    triggerConfetti(20);
    soundEffects.playWin();
  }
};

export default { triggerConfetti, soundEffects, celebrateWin };
